import { ReactNode } from 'react'
import { LucideIcon } from 'lucide-react'

// DS v2 stat tile — the shared, semantic-token-only stat primitive for
// migrated surfaces (Task Management, Broker Performance, Truck Availability).
// Lives in `components/ds2` so any feature can consume it via the barrel
// (`import { StatTileV2 } from 'components/ds2'`) without reaching across
// feature internals. Replaces the shared fleet `features/fleet/components/KpiCard`
// for these surfaces; the 18+ pages still on `KpiCard` (FleetOverviewPage,
// SafetyDashboardPage, TruckPLKpiCards, …) keep the legacy look until `KpiCard`
// itself is migrated to v2 tokens.
//
// Two `row` visual variants, chosen by whether an `icon` is supplied:
//   • icon omitted  → colored accent bar + label + value
//   • icon supplied → soft-tinted icon tile + label + value
// A compact `stack` layout (tone dot + label on top, monospace value below) backs the
// truck-availability bottom cards, so those tiles share this primitive instead of a
// bespoke copy.

export type StatTone = 'accent' | 'success' | 'danger' | 'warning' | 'default'

// Solid accent bar (no-icon variant).
const BAR_CLASSES: Record<StatTone, string> = {
    accent: 'bg-accent',
    success: 'bg-success',
    danger: 'bg-danger',
    warning: 'bg-warning',
    default: 'bg-default',
}

// Soft-tinted icon tile (icon variant).
const ICON_TONE_CLASSES: Record<StatTone, string> = {
    accent: 'bg-accent-soft text-accent-soft-foreground',
    success: 'bg-success-soft text-success-soft-foreground',
    danger: 'bg-danger-soft text-danger-soft-foreground',
    warning: 'bg-warning-soft text-warning-soft-foreground',
    default: 'bg-default text-foreground-muted',
}

interface StatTileProps {
    title: string
    /** A string when the number carries a unit the tile must not invent — `26.8%`, `3h 12m`. */
    value: number | string
    tone: StatTone
    icon?: LucideIcon
    /** 'row' (default): indicator on the left, label + value stacked to its right (dashboard tiles).
     *  'stack': a small tone dot beside the label, monospace value below (truck-availability tiles). */
    layout?: 'row' | 'stack'
    /** One line under the value — a period-over-period delta, or what exactly was counted. 'row' only. */
    footer?: ReactNode
    /** 'stack' only: the value in the monospace face (the truck-availability tiles). Off, it reads in the page's own font with tabular figures. */
    monospaceValue?: boolean
}

export const StatTileV2 = ({ title, value, tone, icon: Icon, layout = 'row', footer, monospaceValue = true }: StatTileProps) => {
    if (layout === 'stack') {
        return (
            <div className="rounded-2xl bg-surface p-4 shadow-surface">
                <div className="flex items-center gap-1.5">
                    <span className={`h-2 w-2 shrink-0 rounded-full ${BAR_CLASSES[tone]}`} />
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">{title}</span>
                </div>
                <div className={`mt-1 text-2xl font-bold tabular-nums text-foreground ${monospaceValue ? 'font-mono' : ''}`}>{value}</div>
            </div>
        )
    }

    return (
        <div className="flex items-center gap-3 rounded-2xl bg-surface p-4 shadow-surface">
            {Icon ? (
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${ICON_TONE_CLASSES[tone]}`}>
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                </span>
            ) : (
                <span className={`h-9 w-1 shrink-0 rounded-full ${BAR_CLASSES[tone]}`} />
            )}
            <div className="min-w-0">
                <p className="truncate text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">{title}</p>
                <p className="text-2xl font-bold leading-tight text-foreground">{value}</p>
                {footer ? <div className="mt-0.5">{footer}</div> : null}
            </div>
        </div>
    )
}
